import { StandardEvents } from '@shopify/events';

/*
 * at-header.js — AllTerra Central global header
 * Behaviors: sticky top-bar hide-on-scroll, mega-menu open/close (hover + focus + click),
 * rail category swapping, keyboard/ARIA support, live cart count.
 */

const OPEN_DELAY = 80;
const CLOSE_DELAY = 180;
const SCROLL_THRESHOLD = 8;

class AtHeader extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
    this.activeKey = null;
    this.openTimer = null;
    this.closeTimer = null;

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocPointerDown = this.onDocPointerDown.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onResize = this.onResize.bind(this);

    this.bindTriggers();
    this.bindHoverRegion();
    this.bindCategorySwap();
    this.bindMobile();

    // The theme scrolls inside a wrapper element (html/body are overflow:hidden),
    // so listen in the capture phase on document to catch scroll from any scroller.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onDocPointerDown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('resize', this.onResize);
    this.onScroll();
    this.syncScrollbarWidth();
    this.syncHeaderHeight();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onDocPointerDown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('resize', this.onResize);
  }

  /* ---- scrollbar-width alignment ---- */
  // The header is fixed to the viewport (full width) while page content sits in
  // the .page-wrapper scroll container minus its scrollbar. Publish that scrollbar
  // width so the header can reserve it and stay aligned with the content past
  // 1440px. 0 with overlay scrollbars (macOS), ~11-17px on Windows/Linux.
  onResize() {
    this.syncScrollbarWidth();
    this.syncHeaderHeight();
  }

  syncScrollbarWidth() {
    const wrapper = document.querySelector('.page-wrapper');
    const width = wrapper ? Math.max(0, wrapper.offsetWidth - wrapper.clientWidth) : 0;
    document.documentElement.style.setProperty('--at-scrollbar-width', `${width}px`);
  }

  // Publish the header's full (unscrolled) height so interior templates can offset
  // their content beneath the fixed header (Horizon's native --header-height script
  // targets <header-component>, which this custom header replaces). Measured only at
  // the top so the hide-on-scroll top-bar collapse never jitters the offset.
  syncHeaderHeight() {
    if (this.classList.contains('is-scrolled')) return;
    const height = Math.round(this.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--at-header-height', `${height}px`);
    }
  }

  /* ---- live cart count ---- */
  // The nav "Cart (n)" count is server-rendered; keep it in sync off the theme's
  // cart event so it reflects add/update/remove without a page reload. Mirrors
  // cart-icon.js (cart.totalQuantity), event-driven — no direct cross-component call.
  onCartUpdate(event) {
    event?.promise?.then(
      ({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        const el = this.querySelector('[data-at-cart-count]');
        if (el && Number.isFinite(count)) el.textContent = String(count);
      },
      () => {}
    );
  }

  /* ---- sticky / top-bar hide ---- */
  onScroll(event) {
    const target = event && event.target && event.target !== document ? event.target : null;
    const top = target ? target.scrollTop : (window.scrollY || (document.scrollingElement || document.documentElement).scrollTop || 0);
    this.classList.toggle('is-scrolled', top > SCROLL_THRESHOLD);
  }

  /* ---- mega menu open/close ---- */
  bindTriggers() {
    this.triggers.forEach((trigger) => {
      const key = trigger.getAttribute('data-mega-trigger');
      trigger.addEventListener('pointerenter', () => this.scheduleOpen(key));
      trigger.addEventListener('focus', () => this.open(key));
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.activeKey === key) {
          this.close();
        } else {
          this.open(key);
        }
      });
    });
  }

  bindHoverRegion() {
    this.addEventListener('pointerleave', () => this.scheduleClose());
    this.addEventListener('pointerenter', () => {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    });
  }

  scheduleOpen(key) {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(key), OPEN_DELAY);
  }

  scheduleClose() {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  open(key) {
    if (!key) return;
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    const search = document.querySelector('at-search');
    if (search && !search.hidden && search.close) search.close();
    this.activeKey = key;
    this.classList.add('is-open');

    this.panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-mega-panel') !== key;
    });
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', trigger.getAttribute('data-mega-trigger') === key ? 'true' : 'false');
    });
  }

  close() {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.activeKey = null;
    this.classList.remove('is-open');
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.drawer && !this.drawer.hidden) {
      this.closeMobile();
    } else if (this.activeKey) {
      const trigger = this.triggers.find((t) => t.getAttribute('data-mega-trigger') === this.activeKey);
      this.close();
      if (trigger) trigger.focus();
    }
  }

  /* ---- mobile drawer + drill-down ---- */
  bindMobile() {
    this.drawer = this.querySelector('[data-mobile-drawer]');
    if (!this.drawer) return;
    this.mobileToggle = this.querySelector('[data-mobile-open]');
    this.mobileLevels = Array.from(this.querySelectorAll('[data-mobile-level]'));

    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', () => this.openMobile());
    }
    const back = this.querySelector('[data-mobile-back]');
    if (back) back.addEventListener('click', () => this.mobileBack());

    this.querySelectorAll('[data-mobile-drill]').forEach((btn) => {
      btn.addEventListener('click', () => this.mobileDrill(btn.getAttribute('data-mobile-drill')));
    });
  }

  openMobile() {
    this.drawer.hidden = false;
    this.showMobileLevel('root');
    if (this.mobileToggle) this.mobileToggle.setAttribute('aria-expanded', 'true');
  }

  closeMobile() {
    this.drawer.hidden = true;
    this.drawer.classList.remove('is-nested');
    this.showMobileLevel('root');
    if (this.mobileToggle) {
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      this.mobileToggle.focus();
    }
  }

  mobileDrill(key) {
    if (!key) return;
    this.showMobileLevel(key);
    this.drawer.classList.add('is-nested');
  }

  mobileBack() {
    if (this.drawer.classList.contains('is-nested')) {
      this.drawer.classList.remove('is-nested');
      this.showMobileLevel('root');
    } else {
      this.closeMobile();
    }
  }

  showMobileLevel(level) {
    this.mobileLevels.forEach((el) => {
      const active = el.getAttribute('data-mobile-level') === level;
      el.classList.toggle('is-active', active);
      el.hidden = !active;
    });
  }

  onDocPointerDown(event) {
    if (this.activeKey && !this.contains(event.target)) {
      this.close();
    }
  }

  /* ---- rail category swap ---- */
  bindCategorySwap() {
    this.panels.forEach((panel) => {
      const cats = Array.from(panel.querySelectorAll('[data-cat-trigger]'));
      const groups = Array.from(panel.querySelectorAll('[data-cat-group]'));
      cats.forEach((cat) => {
        const activate = () => {
          const catKey = cat.getAttribute('data-cat-trigger');
          cats.forEach((c) => {
            const on = c === cat;
            c.classList.toggle('is-active', on);
            if (c instanceof HTMLButtonElement) c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          groups.forEach((g) => {
            g.classList.toggle('is-active', g.getAttribute('data-cat-group') === catKey);
          });
        };
        cat.addEventListener('pointerenter', activate);
        // Linked categories (<a>) navigate; unlinked ones (<button>) only swap.
        if (cat instanceof HTMLButtonElement) {
          cat.addEventListener('click', (event) => {
            event.preventDefault();
            activate();
          });
        } else {
          // Keyboard: Enter follows the link; Space / ArrowRight show the
          // group and move focus into it (Tab alone never swaps groups, so
          // the visible group's tiles stay reachable).
          cat.addEventListener('keydown', (event) => {
            if (event.key !== ' ' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            activate();
            const catKey = cat.getAttribute('data-cat-trigger');
            const group = groups.find((g) => g.getAttribute('data-cat-group') === catKey);
            group?.querySelector('a, button')?.focus();
          });
        }
      });
    });
  }
}

if (!customElements.get('at-header')) {
  customElements.define('at-header', AtHeader);
}
